import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Repeat, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { StatusChip, adminButton, adminLink } from '@/components/admin/ui';
import {
  createSeriesFromJob,
  invalidateRecurring,
  skipOccurrence,
  useRecurringSeriesById,
} from '@/hooks/useRecurringSeries';
import { SERIES_STATUS_LABELS, canSkipVisit, describeSchedule } from '@/lib/recurrence';
import { cn } from '@/lib/utils';
import { RepeatSettingsFields } from './RepeatSettingsFields';
import { DEFAULT_REPEAT_SETTINGS, repeatSettingsError, toCreateSeriesInput, type RepeatSettings } from './repeat-settings';
import { errorText } from './visit-edit';

export interface RepeatableJob {
  id: string;
  status: string;
  scheduled_date: string;
  /** undefined = not loaded with the job (the panel looks it up). */
  series_id?: string | null;
  series_occurrence_date?: string | null;
  is_exception?: boolean | null;
}

export type SeriesLink = Pick<RepeatableJob, 'id' | 'series_id' | 'series_occurrence_date' | 'is_exception'>;

interface RepeatVisitPanelProps {
  job: RepeatableJob;
  canWrite: boolean;
  /** Called after a change to the visit/series so the calendar can refetch. */
  onChanged: () => void;
  /** Close the job dialog (after skipping, the visit no longer exists). */
  onClose?: () => void;
  /** Receives the series link when it had to be looked up. */
  onResolved?: (link: SeriesLink) => void;
}

/**
 * Repeat-clean section of the job dialog. For a series visit: the schedule,
 * "Skip this visit" and a link to manage it. For a one-off job: "Make it
 * repeat".
 */
export function RepeatVisitPanel({ job: jobProp, canWrite, onChanged, onClose, onResolved }: RepeatVisitPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Some lists load jobs without the series columns; fetch the link once.
  const needsLookup = jobProp.series_id === undefined;
  const { data: looked } = useQuery({
    queryKey: ['job-series-link', jobProp.id],
    enabled: needsLookup,
    queryFn: async (): Promise<SeriesLink | null> => {
      const { data, error } = await supabase.from('jobs').select('*').eq('id', jobProp.id).maybeSingle();
      if (error) throw error;
      const row = (data ?? null) as (SeriesLink & Record<string, unknown>) | null;
      return row
        ? { id: jobProp.id, series_id: row.series_id ?? null, series_occurrence_date: row.series_occurrence_date ?? null, is_exception: row.is_exception ?? false }
        : null;
    },
  });
  useEffect(() => {
    if (needsLookup && looked) onResolved?.(looked);
  }, [needsLookup, looked, onResolved]);
  const job: RepeatableJob = needsLookup && looked ? { ...jobProp, ...looked } : jobProp;
  const { data: series } = useRecurringSeriesById(job.series_id);
  const [busy, setBusy] = useState<'skip' | 'create' | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [settings, setSettings] = useState<RepeatSettings>({ ...DEFAULT_REPEAT_SETTINGS, frequency: 'weekly' });

  if (job.series_id) {
    const handleSkip = async () => {
      if (!job.series_id || !job.series_occurrence_date) return;
      if (!window.confirm('Skip this visit? The rest of the repeat clean carries on as normal.')) return;
      setBusy('skip');
      try {
        await skipOccurrence(job.series_id, job.series_occurrence_date);
        invalidateRecurring(queryClient);
        toast({ title: 'Visit skipped', description: 'We won’t add it back.' });
        onChanged();
        onClose?.();
      } catch (error) {
        toast({ title: 'Couldn’t skip this visit', description: errorText(error), variant: 'destructive' });
      } finally {
        setBusy(null);
      }
    };

    return (
      <div className="rounded-xl border border-border bg-secondary/40 p-3" data-testid="repeat-visit-panel">
        <div className="flex flex-wrap items-center gap-2">
          <Repeat className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-[13px] font-semibold text-foreground">Repeat clean</span>
          {series && series.status !== 'active' && (
            <StatusChip tone={series.status === 'ended' ? 'grey' : 'amber'}>{SERIES_STATUS_LABELS[series.status]}</StatusChip>
          )}
          {job.is_exception && <StatusChip tone="violet" dot={false}>Changed for this visit</StatusChip>}
        </div>
        {series && (
          <p className="mt-1 text-[12.5px] text-muted-foreground" data-testid="repeat-visit-schedule">
            {describeSchedule(series.frequency, series.anchor_date, series.start_time)}
            {series.end_date ? ` · until ${series.end_date.split('-').reverse().join('/')}` : ''}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {canWrite && canSkipVisit(job) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(adminButton.outline, 'h-8')}
              onClick={handleSkip}
              disabled={busy !== null}
            >
              {busy === 'skip' ? <Loader2 className="animate-spin" /> : <SkipForward />}
              Skip this visit
            </Button>
          )}
          <Link to={`/admin/repeat-cleans?series=${job.series_id}`} className={adminLink}>
            Manage repeat clean →
          </Link>
        </div>
      </div>
    );
  }

  if (!canWrite || job.status === 'cancelled' || job.series_id === undefined) return null;

  const handleCreate = async () => {
    const problem = repeatSettingsError(settings, job.scheduled_date);
    if (problem) {
      toast({ title: 'Check the repeat settings', description: problem, variant: 'destructive' });
      return;
    }
    const input = toCreateSeriesInput(job.id, settings);
    if (!input) return;
    setBusy('create');
    try {
      await createSeriesFromJob(input);
      invalidateRecurring(queryClient);
      toast({ title: 'Repeat clean set up', description: 'Upcoming visits have been added to the calendar.' });
      setShowCreate(false);
      onChanged();
      onClose?.();
    } catch (error) {
      toast({ title: 'Couldn’t set up the repeat clean', description: errorText(error), variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  if (!showCreate) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(adminButton.ghost, 'h-8 px-2')}
        onClick={() => setShowCreate(true)}
        data-testid="make-repeat"
      >
        <Repeat />
        Make this a repeat clean
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-border bg-secondary/40 p-3" data-testid="make-repeat-panel">
      <RepeatSettingsFields value={settings} onChange={setSettings} firstVisit={job.scheduled_date} idPrefix={`make-repeat-${job.id}`} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className={cn(adminButton.outline, 'h-8')} onClick={() => setShowCreate(false)}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className={cn(adminButton.primary, 'h-8')}
          onClick={handleCreate}
          disabled={busy !== null || settings.frequency === 'none'}
        >
          {busy === 'create' && <Loader2 className="animate-spin" />}
          Set up repeat clean
        </Button>
      </div>
    </div>
  );
}
